/**
 * House Keeping Dashboard - Mobile-friendly view
 * Shows room cleaning tasks with status management
 */
import { useState, useEffect, useCallback } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Clock, CheckCircle2, AlertTriangle, Play, Check, 
  MapPin, Timer, RefreshCw, Bed, Sparkles, Users,
  AlertCircle, Home
} from 'lucide-react';

// Calculate time difference in minutes
const getTimeDiff = (startTime) => {
  if (!startTime) return 0;
  const start = new Date(startTime);
  const now = new Date();
  return Math.floor((now - start) / 60000);
};

// Format time for display
const formatTime = (minutes) => {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h ${mins}m`;
};

// Priority colors
const priorityColors = {
  high: 'bg-red-100 text-red-700 border-red-300',
  medium: 'bg-yellow-100 text-yellow-700 border-yellow-300',
  low: 'bg-green-100 text-green-700 border-green-300',
  checkout: 'bg-purple-100 text-purple-700 border-purple-300',
  stayover: 'bg-blue-100 text-blue-700 border-blue-300',
};

// Task Card Component
const TaskCard = ({ task, onStart, onComplete, onReport }) => {
  const [elapsed, setElapsed] = useState(0);
  const isStarted = task.status === 'in_progress';
  const isCompleted = task.status === 'completed' || task.status === 'cleaned';
  const estimatedTime = task.estimated_time || 30; // Default 30 mins
  
  // Timer for in-progress tasks
  useEffect(() => {
    if (!isStarted) return;
    
    const timer = setInterval(() => {
      const startedAt = task.started_at || task.updated_at;
      setElapsed(getTimeDiff(startedAt));
    }, 1000);
    
    return () => clearInterval(timer);
  }, [isStarted, task.started_at, task.updated_at]);

  const isOverdue = elapsed > estimatedTime;

  return (
    <Card className={`mb-3 ${isOverdue && isStarted ? 'border-red-300 bg-red-50' : ''}`}>
      <CardContent className="p-4">
        {/* Header with room and priority */}
        <div className="flex justify-between items-start mb-3">
          <div className="flex items-center gap-2">
            <div className={`p-2 rounded-lg ${task.task_type === 'checkout' ? 'bg-purple-100' : 'bg-blue-100'}`}>
              {task.task_type === 'checkout' ? (
                <Home className="h-5 w-5 text-purple-600" />
              ) : (
                <Bed className="h-5 w-5 text-blue-600" />
              )}
            </div>
            <div>
              <span className="font-bold text-xl">{task.room_number || 'N/A'}</span>
              <p className="text-xs text-gray-500">{task.room_type || 'Standard'}</p>
            </div>
          </div>
          <Badge variant="outline" className={priorityColors[task.priority] || priorityColors.medium}>
            {task.task_type === 'checkout' ? 'Checkout' : 'Stayover'}
          </Badge>
        </div>

        {/* Task details */}
        <div className="bg-gray-50 rounded-lg p-3 mb-3">
          <div className="flex items-center gap-2 text-sm">
            <Sparkles className="h-4 w-4 text-orange-500" />
            <span className="font-medium">
              {task.task_type === 'checkout' ? 'Full Cleaning Required' : 'Daily Cleaning'}
            </span>
          </div>
          {task.special_requests && (
            <p className="text-sm text-gray-600 mt-2 italic">"{task.special_requests}"</p>
          )}
          {task.notes && (
            <p className="text-xs text-gray-500 mt-1">{task.notes}</p>
          )}
        </div>

        {/* Guest info if stayover */}
        {task.task_type === 'stayover' && task.guest_name && (
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
            <Users className="h-4 w-4" />
            <span>Guest: {task.guest_name}</span>
          </div>
        )}

        {/* Timer display */}
        {isStarted && !isCompleted && (
          <div className={`flex items-center gap-2 mb-3 p-2 rounded-lg ${isOverdue ? 'bg-red-100' : 'bg-blue-50'}`}>
            <Timer className={`h-4 w-4 ${isOverdue ? 'text-red-600' : 'text-blue-600'}`} />
            <span className={`text-sm font-medium ${isOverdue ? 'text-red-600' : 'text-blue-600'}`}>
              {formatTime(elapsed)} / {estimatedTime}m
            </span>
            {isOverdue && <AlertTriangle className="h-4 w-4 text-red-500 ml-auto" />}
          </div>
        )}

        {/* Status and Actions */}
        <div className="flex justify-between items-center">
          <div>
            {task.status === 'pending' && (
              <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                <Clock className="h-3 w-3 mr-1" />
                Pending
              </Badge>
            )}
            {isStarted && !isCompleted && (
              <Badge variant="outline" className={isOverdue ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'}>
                <Play className="h-3 w-3 mr-1" />
                In Progress
              </Badge>
            )}
            {isCompleted && (
              <Badge variant="outline" className="bg-green-50 text-green-700">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Completed
              </Badge>
            )}
          </div>
          
          <div className="flex gap-2">
            {task.status === 'pending' && (
              <Button 
                size="sm" 
                onClick={() => onStart(task.id)}
                className="bg-blue-500 hover:bg-blue-600"
              >
                <Play className="h-4 w-4 mr-1" />
                Start
              </Button>
            )}
            {isStarted && !isCompleted && (
              <>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => onReport(task.id)}
                  className="text-orange-600 border-orange-300"
                >
                  <AlertCircle className="h-4 w-4" />
                </Button>
                <Button 
                  size="sm" 
                  onClick={() => onComplete(task.id)}
                  className="bg-green-500 hover:bg-green-600"
                >
                  <Check className="h-4 w-4 mr-1" />
                  Done
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Completion time for done tasks */}
        {isCompleted && task.completed_at && (
          <div className="mt-3 pt-3 border-t text-xs text-gray-500 flex justify-between">
            <span>Completed: {new Date(task.completed_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
            {task.duration && <span>Duration: {formatTime(task.duration)}</span>}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function HouseKeepingDashboard({ user }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [refreshing, setRefreshing] = useState(false);

  const loadTasks = useCallback(async () => {
    try {
      // Try housekeeping-tasks endpoint first, fallback to generating from rooms
      let tasksData = [];
      try {
        const res = await api.get('/housekeeping-tasks/my-tasks');
        tasksData = res.data || [];
      } catch (e) {
        // Fallback: Generate tasks from rooms that need cleaning
        const roomsRes = await api.get('/rooms');
        const bookingsRes = await api.get('/bookings?status=checked_out,checked_in').catch(() => ({ data: [] }));
        
        const rooms = roomsRes.data || [];
        const bookings = bookingsRes.data || [];
        
        // Create tasks for checkout rooms and stayover rooms
        tasksData = rooms.map(room => {
          const booking = bookings.find(b => b.room_id === room.id);
          const isCheckout = booking?.status === 'checked_out';
          const isStayover = booking?.status === 'checked_in';
          
          if (!isCheckout && !isStayover && room.status === 'available') {
            return null;
          }
          
          return {
            id: `task-${room.id}`,
            room_id: room.id,
            room_number: room.room_number,
            room_type: room.room_type_name || room.room_type,
            floor: room.floor,
            task_type: isCheckout ? 'checkout' : 'stayover',
            status: room.cleaning_status || 'pending',
            priority: isCheckout ? 'high' : 'medium',
            guest_name: booking?.guest_name,
            estimated_time: isCheckout ? 45 : 20,
            created_at: new Date().toISOString(),
          };
        }).filter(Boolean);
      }
      
      setTasks(tasksData);
    } catch (error) {
      console.error('Failed to load tasks:', error);
      toast.error('Failed to load housekeeping tasks');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadTasks();
    // Auto-refresh every 60 seconds
    const interval = setInterval(loadTasks, 60000);
    return () => clearInterval(interval);
  }, [loadTasks]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadTasks();
  };

  const handleStart = async (taskId) => {
    try {
      await api.put(`/housekeeping-tasks/${taskId}/start`);
      toast.success('Task started! Timer running.');
      loadTasks();
    } catch (error) {
      // Fallback: update local state
      setTasks(prev => prev.map(t => 
        t.id === taskId ? { ...t, status: 'in_progress', started_at: new Date().toISOString() } : t
      ));
      toast.success('Task started!');
    }
  };

  const handleComplete = async (taskId) => {
    try {
      const res = await api.put(`/housekeeping-tasks/${taskId}/complete`);
      const withinTime = res.data?.within_time;
      toast.success(withinTime ? 'Task completed on time!' : 'Task completed (overtime)');
      loadTasks();
    } catch (error) {
      // Fallback: update local state
      setTasks(prev => prev.map(t => 
        t.id === taskId ? { ...t, status: 'completed', completed_at: new Date().toISOString() } : t
      ));
      toast.success('Task completed!');
    }
  };

  const handleReport = async (taskId) => {
    const issue = prompt('Report an issue with this room:');
    if (!issue) return;
    
    try {
      await api.post(`/housekeeping-tasks/${taskId}/report`, { issue });
      toast.success('Issue reported to management');
    } catch (error) {
      toast.info('Issue noted');
    }
  };

  // Filter tasks by tab
  const pendingTasks = tasks.filter(t => t.status === 'pending');
  const activeTasks = tasks.filter(t => t.status === 'in_progress');
  const completedTasks = tasks.filter(t => ['completed', 'cleaned'].includes(t.status));

  // Sort pending by priority
  const sortedPending = [...pendingTasks].sort((a, b) => {
    const priorityOrder = { high: 0, checkout: 0, medium: 1, stayover: 1, low: 2 };
    return (priorityOrder[a.priority] || 1) - (priorityOrder[b.priority] || 1);
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto text-orange-500 mb-2" />
          <p className="text-gray-500">Loading tasks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100" data-testid="housekeeping-dashboard">
      {/* Mobile Header */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-4 sticky top-0 z-10">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold">Housekeeping</h1>
            <p className="text-orange-100 text-sm">Welcome, {user?.name || 'Staff'}</p>
          </div>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={handleRefresh}
            className="text-white hover:bg-orange-400"
            disabled={refreshing}
          >
            <RefreshCw className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-white border-b px-4 py-3 grid grid-cols-3 gap-2 text-center">
        <div className="p-2 bg-yellow-50 rounded-lg">
          <p className="text-2xl font-bold text-yellow-600">{pendingTasks.length}</p>
          <p className="text-xs text-yellow-700">Pending</p>
        </div>
        <div className="p-2 bg-blue-50 rounded-lg">
          <p className="text-2xl font-bold text-blue-600">{activeTasks.length}</p>
          <p className="text-xs text-blue-700">In Progress</p>
        </div>
        <div className="p-2 bg-green-50 rounded-lg">
          <p className="text-2xl font-bold text-green-600">{completedTasks.length}</p>
          <p className="text-xs text-green-700">Done Today</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 p-1 bg-white border-b">
          <TabsTrigger value="pending" className="data-[state=active]:bg-yellow-100 data-[state=active]:text-yellow-700">
            <Clock className="h-4 w-4 mr-1" />
            Pending ({pendingTasks.length})
          </TabsTrigger>
          <TabsTrigger value="active" className="data-[state=active]:bg-blue-100 data-[state=active]:text-blue-700">
            <Play className="h-4 w-4 mr-1" />
            Active ({activeTasks.length})
          </TabsTrigger>
          <TabsTrigger value="completed" className="data-[state=active]:bg-green-100 data-[state=active]:text-green-700">
            <CheckCircle2 className="h-4 w-4 mr-1" />
            Done ({completedTasks.length})
          </TabsTrigger>
        </TabsList>

        <ScrollArea className="h-[calc(100vh-220px)]">
          <div className="p-4">
            <TabsContent value="pending" className="mt-0">
              {sortedPending.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Sparkles className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                  <p>All caught up!</p>
                  <p className="text-sm">No pending cleaning tasks</p>
                </div>
              ) : (
                sortedPending.map(task => (
                  <TaskCard 
                    key={task.id} 
                    task={task} 
                    onStart={handleStart}
                    onComplete={handleComplete}
                    onReport={handleReport}
                  />
                ))
              )}
            </TabsContent>

            <TabsContent value="active" className="mt-0">
              {activeTasks.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Timer className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                  <p>No active tasks</p>
                  <p className="text-sm">Start a pending task to begin</p>
                </div>
              ) : (
                activeTasks.map(task => (
                  <TaskCard 
                    key={task.id} 
                    task={task} 
                    onStart={handleStart}
                    onComplete={handleComplete}
                    onReport={handleReport}
                  />
                ))
              )}
            </TabsContent>

            <TabsContent value="completed" className="mt-0">
              {completedTasks.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <CheckCircle2 className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                  <p>No completed tasks today</p>
                </div>
              ) : (
                completedTasks.slice(0, 20).map(task => (
                  <TaskCard 
                    key={task.id} 
                    task={task} 
                    onStart={handleStart}
                    onComplete={handleComplete}
                    onReport={handleReport}
                  />
                ))
              )}
            </TabsContent>
          </div>
        </ScrollArea>
      </Tabs>
    </div>
  );
}
